import { text } from '@clack/prompts'
import { Command } from 'commander'

import { apiService } from '@/helpers'
import { output } from '@/helpers/std-out'
import { getFileOrUrl } from '@/domains/document/helpers'
import { slugify } from '@scalar-org/helpers/url'

export const CreateCommand = () => {
  const cmd = new Command('create')

  cmd.description('Create a document on scalar registry')
  cmd.argument('[file]', 'file to upload')
  cmd.option('--namespace <namespace>', 'Namespace')
  cmd.option('--title <title>', 'Document title')
  cmd.option('--description <description>', 'Document description')
  cmd.option('--doc-version <doc-version>', 'Document version')
  cmd.action(
    async (filePath, { namespace, title, description, docVersion }) => {
      const apiClient = await apiService()

      if (!filePath) {
        return output
          .error()
          .title('Invalid argument')
          .message('Please provide a file path')
          .exit('error')
      }

      const document = await getFileOrUrl(filePath)
      if (!document) {
        return output
          .error()
          .title('Invalid file')
          .message('Please provide a valid file path')
          .exit('error')
      }

      const shouldPrompt = ![namespace, title, description, docVersion].some(
        (it) => it !== undefined,
      )

      let namespaceValue = namespace
      let titleValue = title
      let descriptionValue = description
      let versionValue = docVersion

      if (!namespaceValue) {
        namespaceValue = await text({
          message: 'What is the namespace of your document?',
          validate(value: string) {
            if (value.length === 0) {
              return 'Value is required!'
            }

            return
          },
        })
      }

      if (!titleValue) {
        titleValue = await text({
          message: 'What is the title of your document?',
          validate(value: string) {
            if (value.length === 0) {
              return 'Value is required!'
            }

            return
          },
        })
      }

      if (!descriptionValue && shouldPrompt) {
        descriptionValue =
          (await text({
            message: 'What is the description of your document?',
          })) ?? undefined
      }

      if (!versionValue) {
        versionValue = await text({
          message: 'What is the version of your document?',
          validate(value: string) {
            if (value.length === 0) {
              return 'Value is required!'
            }

            return
          },
        })
      }

      const logger = output.info().line().loader('Creating the api document')
      const createdDocument = await apiClient.managedDocs.create(
        namespaceValue,
        {
          title: titleValue,
          description: descriptionValue,
          version: versionValue,
          slug: slugify(titleValue),
          isPrivate: true,
          document,
        },
      )

      // Display error message from the server
      if (createdDocument.error) {
        return output.error().title(createdDocument.message).exit('error')
      }

      // Display success message
      logger
        .update('loader', 0, { content: 'Successfully created the document.' })
        .print()
    },
  )

  return cmd
}
